import { Component, OnInit, OnDestroy } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { AwardsService, AwardProgramInfo } from 'services/awards/awards.service';
import { ApplicationService, NomineeTypeInfo, SchoolInfo, SchoolNomineeInfo, NomineeInfo } from 'services/application/application.service';
import { DialogService } from 'services/dialogs/dialogs.service';
import { ErrorService } from 'services/error/error.service';
import { SettingsService } from 'services/settings/settings.service';
import { SplashService } from 'services/splash/splash.service';
import { UserService } from 'services/user/user.service';
import { TitleCasePipe } from 'pipes/titlecase.pipe';

@Component({
    selector: 'app-nominees',
    styleUrls: ['nominees.component.scss'],
    templateUrl: 'nominees.component.html',
})

export class NomineesComponent implements OnInit, OnDestroy {
    private _subrelease: Subject<void> = new Subject<void>();

    AllNomineeTypes: NomineeTypeInfo[];
    AvailableSchools: SchoolInfo[];
    AwardInfo: AwardProgramInfo;
    CurrentSchool: SchoolInfo;
    CurrentSchoolYear: string;
    Nominations: SchoolNomineeInfo[];
    TentativeSchool: SchoolInfo;
    ViewReady: boolean;
    notEligible: boolean;

    constructor(
        private appSvc: ApplicationService,
        private awardSvc: AwardsService,
        private dlgSvc: DialogService,
        private errorSvc: ErrorService,
        private settingsSvc: SettingsService,
        private splashSvc: SplashService,
        private titleCasePipe: TitleCasePipe,
        private userSvc: UserService
    ) {}

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;

        self.AllNomineeTypes = null;
        self.AvailableSchools = null;
        self.AvailableSchools = null;
        self.AwardInfo = null;
        self.CurrentSchool = null;
        self.CurrentSchoolYear = null;
        self.TentativeSchool = null;
        self.ViewReady = false;
        self.notEligible = false;

        // Set up a subscription on the current award program, so we know which schools are available (if that's a thing),
        // and also on the current school year.
		combineLatest(self.awardSvc.CurrentAwardsSub, self.settingsSvc.CurrentSchoolYearSubject).pipe(
            takeUntil(self._subrelease),
            map(_pck => {
                return {
                    Awards: _pck[0],
                    Year: _pck[1],
                };
            }),
            filter(pck => pck && !!pck.Awards)
        ).subscribe(packet => {
            // Get the current award program
            self.AwardInfo = packet.Awards;
            self.CurrentSchoolYear = packet.Year;

            // Load everything we need for this page
            const allNomTypesObs = self.appSvc.GetNomineeTypes();
            const curSchoolObs = self.settingsSvc.CurrentSchoolSubject;
			const availSchoolsObs = self.appSvc.GetAvailableSchools(self.AwardInfo.AwardID, self.CurrentSchoolYear);
			const schoolEligibilityObs = self.appSvc.GetSchoolEligibility(self.AwardInfo.AwardID, self.CurrentSchoolYear);

            combineLatest(allNomTypesObs, curSchoolObs, availSchoolsObs, schoolEligibilityObs).pipe(
                takeUntil(self._subrelease),
                map(_spck => {
                    return {
                        NomineeTypes: _spck[0],
                        Currentschool: _spck[1],
                        AvailableSchools: _spck[2],
                        EligibilityList: _spck[3]
                    };
                }),
                filter(p => !!p && !!p.NomineeTypes && !!p.AvailableSchools && !!p.AvailableSchools.length)
            ).subscribe(spacket => {
                self.AllNomineeTypes = spacket.NomineeTypes.filter(n => n.AwardID === self.AwardInfo.AwardID) || [];
				self.CurrentSchool = spacket.Currentschool;
                self.AvailableSchools = spacket.AvailableSchools;
                let eligibleSchools = spacket.EligibilityList;

                // Early exit if no user
                if (!self.userSvc.User) {
                    return;
                }

                if (eligibleSchools.findIndex(e => e.Display === self.CurrentSchool.SchoolNumber && e.Value === self.AwardInfo.AwardID) < 0) {
                    //Display Message
                    this.notEligible = true;
                    return;
				}

                // Load the nominations for the current school.
                self.SetNominations();
            }, error => self.errorSvc.HandleServiceError(error));
        });
    }

    SetNominations() {
        let self = this;

        self.appSvc.GetNominationsByWorkLocation(
            self.userSvc.User.AuthToken,
            self.CurrentSchool.SchoolNumber,
            self.AwardInfo.AwardID,
            self.CurrentSchoolYear
        ).pipe(takeUntil(self._subrelease)).subscribe(noms => {
            //put nominee and nomType together into a SchoolNomineeInfo
            self.Nominations = [];

            for (let n of noms.NomineeType) {
                let nInfo = new SchoolNomineeInfo();

                nInfo.NomineeInfo = noms.Nominees.find(x => x.NomineeTypeID === n.NomineeTypeID && x.GradeLevel === n.GradeLevel);

                switch (n.GradeLevel) {
                    case "STUDENTELEM":
                        n.GradeDisplay = "Elementary School Nominee";
                        break;
                    case "STUDENTMIDDLE":
                        n.GradeDisplay = "Middle School Nominee";
                        break;
                    case "STUDENTHIGH":
                        n.GradeDisplay = "High School Nominee";
                        break;
                    default:
                        n.GradeDisplay = null;
                }

                nInfo.NomineeType = n;
                self.Nominations.push(nInfo);
            }

            // Release the view
            self.ViewReady = true;
        },
            error => self.errorSvc.HandleServiceError(error)
        );
	}

    RemoveNomination(nominee: SchoolNomineeInfo) {
        const self = this;

        self.dlgSvc.OpenConfirmationDialog({
            TitleText: 'Confirmation Requested',
            MessageText: 'Are you sure you want to remove this nominee and delete all form and judge inputs?',
            OKText: 'Yes - Remove Nominee',
            CancelText: 'No - CANCEL'
        }).pipe(takeUntil(self._subrelease)).subscribe(result => {
            if (result === true) {
                self.appSvc.RemoveNominee(
                    self.userSvc.User.AuthToken,
                    self.userSvc.User.CurrentRole,
                    nominee.NomineeInfo.NominationID,
                    self.AwardInfo.AwardID,
					nominee.NomineeInfo.WorkLocation,
					self.CurrentSchoolYear
                ).pipe(takeUntil(self._subrelease)).subscribe(sgroups => {
                    // Toast the success
                    self.splashSvc.SnackbarSuccess('Nominee Removed');

                    // Update the nominated person record
                    self.SetNominations();

                }, error => self.errorSvc.HandleServiceError(error));
            }
        });
	}

    ShowPersonLookup(nom: SchoolNomineeInfo) {
        const self = this;

        // Display the custom dialog box to see if we're getting a new nominee.
        const isChange = nom && !!nom.NomineeInfo;
        let message = '';
        let personType = nom.NomineeType.PersonType;
        let gradeLevel = nom.NomineeType.GradeLevel;

        if (personType == "EMPLOYEE") {
            if (isChange) {
                message =
                    `You are about to change the individual currently selected as your location's ` +
                    `${nom.NomineeType.Description} nominee. The current nominee is <strong>` +
                    `${self.titleCasePipe.transform(nom.NomineeInfo.FirstName)} ` +
                    `${self.titleCasePipe.transform(nom.NomineeInfo.LastName)} (${nom.NomineeInfo.LocalJobTitle})</strong>. ` +
                    `Enter a new SAPID in the box below and press the "Search" button to search for the individual. Once found, ` +
                    `you can press the "Change Nominee" button to change the nominee to the new individual. Once changed, ` +
                    `the previous nominee will no longer have access to the nomination form, and the new individual ` +
                    `will become the nominee for your location and may begin working on their nomination form.`;
            } else {
                message =
                    `You are about to assign an individual to be your location's ${nom.NomineeType.Description} ` +
                    `nominee. Enter an SAPID in the box below and press the "Search" button to search for the individual. ` +
                    `Once found, you can press the "Make Nominee" button to set the individual as the nominee for your ` +
                    `location. Once set, the nominee will be given access to their nomination form.`;
            }
        }
        else if (personType == "STUDENT") {
            if (isChange) {
                message =
                    `You are about to change the individual currently selected as your location's ` +
                    `${nom.NomineeType.Description} nominee. The current nominee is <strong>` +
                    `${self.titleCasePipe.transform(nom.NomineeInfo.FirstName)} ` +
                    `${self.titleCasePipe.transform(nom.NomineeInfo.LastName)}</strong>. ` +
                    `Enter a new StudentID in the box below and press the "Search" button to search for the individual. Once found, ` +
                    `you can press the "Change Nominee" button to change the nominee to the new individual. Once changed, ` +
                    `the previous nominee will no longer have access to the nomination form, and the new individual ` +
                    `will become the nominee for your location and may begin working on their nomination form.`;
            } else {
                message =
                    `You are about to assign an individual to be your location's ${nom.NomineeType.Description} ` +
                    `nominee. Enter an StudentID in the box below and press the "Search" button to search for the individual. ` +
                    `Once found, you can press the "Make Nominee" button to set the individual as the nominee for your ` +
                    `location. Once set, the nominee will be given access to their nomination form.`;
            }
        }
        

        self.dlgSvc.OpenNomineeSearchDialog({
            TitleText: `${nom.NomineeType.Description} Nominee ${isChange ? 'Change' : 'Selection'}`,
            MessageText: message,
            OKText: isChange ? 'Change Nominee' : 'Make Nominee',
			CancelText: 'Cancel',
			ForcedLocationNumber: self.CurrentSchool.SchoolNumberList || self.CurrentSchool.SchoolNumber,
            PersonType: personType,
			GradeLevel: gradeLevel,
			SchoolYear: this.CurrentSchoolYear
        }).pipe(takeUntil(self._subrelease)).subscribe(chosen => {
            if (chosen != null) {
                self.UpsertNomination(chosen, isChange, nom);
            }
        });
    }

    ShowPersonInput(nom: SchoolNomineeInfo) {
        const self = this;

        // Display the custom dialog box to see if we're getting a new nominee.
        const isChange = nom && !!nom.NomineeInfo;
        let message = '';
        let personType = nom.NomineeType.PersonType;
        let gradeLevel = nom.NomineeType.GradeLevel;

        if (personType === "PUBLIC") {
            if (isChange) {
                message =
                    `You are about to change the individual currently selected as your location's ` +
                    `${nom.NomineeType.Description} nominee. The current nominee is <strong>` +
                    `${self.titleCasePipe.transform(nom.NomineeInfo.FirstName)} ` +
                    `${self.titleCasePipe.transform(nom.NomineeInfo.LastName)}</strong>. ` +
                    `Enter a new name and email in the boxes below and ` +
                    `you can press the "Change Nominee" button to change the nominee to the new individual. Once changed, ` +
                    `the new individual ` +
                    `will become the nominee for your location and you may begin working on their nomination form.`;
            } else {
                message =
                    `You are about to assign an individual to be your location's ${nom.NomineeType.Description} ` +
                    `nominee. Enter a name and email in the boxes below and ` +
                    `you can press the "Make Nominee" button to set the individual as the nominee for your ` +
                    `location. Once set, you can begin working on their nomination form.`;
            }
        }
        else {
            if (isChange) {
                message =
                    `<span style="color:red">WARNING! - Only use this method if you are sure the nominee is not in our system. ` +
                    `Check to make sure the StudentID was entered correctly. ` +
                    `If you are a traditional school, it is highly doubtful that you will need this method.</span> <br><br> ` +
                    `You are about to change the individual currently selected as your location's ` +
                    `${nom.NomineeType.Description} nominee. The current nominee is <strong>` +
                    `${self.titleCasePipe.transform(nom.NomineeInfo.FirstName)} ` +
                    `${self.titleCasePipe.transform(nom.NomineeInfo.LastName)}</strong>. ` +
                    `Enter a new name and email in the boxes below and ` +
                    `you can press the "Change Nominee" button to change the nominee to the new individual. Once changed, ` +
                    `the new individual ` +
                    `will become the nominee for your location and you may begin working on their nomination form.`;
            } else {
                message =
                    `You are about to assign an individual to be your location's ${nom.NomineeType.Description} ` +
                    `nominee. Enter a name and email in the boxes below and ` +
                    `you can press the "Make Nominee" button to set the individual as the nominee for your ` +
                    `location. Once set, you can begin working on their nomination form.`;
            }
        }


        self.dlgSvc
            .OpenNomineeInputDialog({
                TitleText: `${nom.NomineeType.Description} Nominee ${isChange ? 'Change' : 'Selection'}`,
                MessageText: message,
                OKText: isChange ? 'Change Nominee' : 'Make Nominee',
                CancelText: 'Cancel'
            })
            .pipe(takeUntil(self._subrelease)).subscribe(chosen => {
                if (chosen != null) {
                    self.UpsertNomination(chosen, isChange, nom);
                }
            });
    }

    UpsertNomination(chosen: NomineeInfo, isChange: boolean, nom: SchoolNomineeInfo) {
        let self = this;

        // The user chose a person. If this is a change, we will set the existing nomination ID value.
        const nomID = isChange ? nom.NomineeInfo.NominationID : null;

        // Call the server to upsert the nomination
        self.appSvc.UpsertNomination(
            self.userSvc.User.AuthToken,
            self.userSvc.User.CurrentRole,
            self.AwardInfo.AwardID,
            nomID,
            chosen.SAPID,
            self.CurrentSchoolYear,
            nom.NomineeType.NomineeTypeID,
            self.CurrentSchool.SchoolNumber,
            chosen.StudentID,
            nom.NomineeType.GradeLevel,
            chosen.FirstName,
            chosen.LastName,
            chosen.Email
        ).pipe(takeUntil(self._subrelease)).subscribe(
            nominee => {
                // Success
                self.splashSvc.SnackbarSuccess(`${nom.NomineeType.Description} nominee assigned successfully.`);

                // Update the nominated person record
                nom.NomineeInfo = nominee;

                self.appSvc.GetNominationsByWorkLocation(self.userSvc.User.AuthToken, self.CurrentSchool.SchoolNumber, self.AwardInfo.AwardID, self.CurrentSchoolYear).subscribe();
            },
            error => self.errorSvc.HandleServiceError(error)
        );
    }

}
